import { Button, Modal, notify } from '@affine/component';
import { DEFAULT_COUNTRY, PhoneInput } from '@affine/component/phone-input';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useState } from 'react';
import type * as RPNInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';

interface PhoneNumberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPhoneNumber?: RPNInput.Value;
  onSave: (phoneNumber: string, country: RPNInput.Country) => void;
}

export const PhoneNumberModal = ({
  open,
  onOpenChange,
  currentPhoneNumber,
  onSave,
}: PhoneNumberModalProps) => {
  const t = useI18n();
  const [phoneNumber, setPhoneNumber] = useState<RPNInput.Value | undefined>(
    currentPhoneNumber
  );

  const [country, setCountry] = useState<RPNInput.Country | undefined>(
    DEFAULT_COUNTRY
  );

  useEffect(() => {
    setPhoneNumber(currentPhoneNumber);
  }, [currentPhoneNumber]);

  const handlePhoneChange = useCallback((value: RPNInput.Value) => {
    setPhoneNumber(value);
  }, []);

  const handleSave = useCallback(() => {
    if (!phoneNumber || !country || !isValidPhoneNumber(phoneNumber)) {
      return;
    }

    try {
      onSave(phoneNumber, country);
      notify.success({
        title: t['docnboard.setting.account.phone-number.update.success'](),
      });
      onOpenChange(false);
    } catch (e) {
      notify.error({
        title: t['docnboard.setting.account.phone-number.update.error'](),
        message: String(e),
      });
    }
  }, [phoneNumber, country, onSave, onOpenChange, t]);

  const setDefaultState = useCallback(() => {
    setPhoneNumber(currentPhoneNumber);
    onOpenChange(false);
  }, [currentPhoneNumber, onOpenChange]);

  const handleCancel = useCallback(
    (open: boolean) => {
      if (!open) setDefaultState();
    },
    [setDefaultState]
  );

  return (
    <Modal
      open={open}
      onOpenChange={handleCancel}
      title={t['docnboard.setting.account.phone-number.modal.title']()}
      contentOptions={{
        className: 'flex flex-col gap-5 p-5',
      }}
    >
      <div>
        <PhoneInput
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={t['docnboard.phone-number.placeholder']()}
          onCountryChange={setCountry}
        />
      </div>
      <div className="flex justify-end gap-3 mt-2">
        <Button onClick={setDefaultState}>{t['Cancel']()}</Button>
        <Button
          onClick={handleSave}
          variant="primary"
          disabled={!isValidPhoneNumber(phoneNumber ?? '') || !country}
        >
          {t['Save']()}
        </Button>
      </div>
    </Modal>
  );
};
